import { useClerk, useOrganization, useSession, useUser } from "@clerk/react";
import { api } from "@clarent/backend/convex/_generated/api";
import { Link, type LinkProps } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import type { ReactNode } from "react";
import { ArchiveIcon, ChevronRightIcon, ClockIcon } from "../components/icons";
import { analyticsAvailable, setRecordingChoice, useRecordingChoice } from "../lib/analytics";
import { formatDay, formatDayTime, formatHours, initials, plural } from "../lib/format";
import { useMachines } from "../lib/machines";
import { SHOP_TIMEZONE } from "../lib/today";

/** Decision D32: the design's Account page, showing only what Clerk and Convex already hold. */
export function AccountPage() {
  const clerk = useClerk();
  const { user } = useUser();
  const { organization, membership } = useOrganization();
  const { session } = useSession();
  const settings = useQuery(api.settings.get);
  const machines = useMachines();
  const archived = useQuery(api.jobs.listArchived);

  if (!user) return <p className="text-sm text-muted">Loading your account…</p>;

  const email = user.primaryEmailAddress;
  const name = user.fullName || email?.emailAddress || "Your account";
  const signInMethods = [email && "Email code", ...user.externalAccounts.map((a) => providerName(a.provider))].filter(Boolean).join(" · ");
  const memberSince = membership?.createdAt ?? user.createdAt;

  return (
    <div className="flex max-w-[1064px] flex-col gap-7">
      <header className="flex items-center gap-5">
        <span className="heading flex size-[76px] shrink-0 items-center justify-center rounded-full bg-brand-tint text-[28px] leading-[34px] tracking-[-0.02em] text-brand-dark">
          {initials(name)}
        </span>
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1 className="heading text-[30px] leading-[105%] tracking-[-0.03em] break-words sm:text-[36px]">{name}</h1>
          {organization && <p className="text-[15px] leading-[18px] text-muted">{organization.name}</p>}
        </div>
      </header>

      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <section className="flex flex-col gap-2 rounded-lg border border-line bg-bg p-6 xl:w-[560px] xl:shrink-0">
          <div className="flex items-center justify-between pb-2.5">
            <h2 className="heading text-lg leading-6 tracking-[-0.02em]">Your details</h2>
            <button type="button" className="btn-secondary" onClick={() => clerk.openUserProfile()}>
              Edit
            </button>
          </div>
          {email && (
            <div className="flex flex-col gap-1.5 rounded-md bg-brand-tint p-4">
              <span className="text-[15px] leading-[18px] font-extrabold break-all">{email.emailAddress}</span>
              {email.verification.status === "verified" ? (
                <span className="pill w-fit bg-ok-tint text-ok-text">
                  <span className="pill-dot bg-ok" />
                  Email verified
                </span>
              ) : (
                <span className="pill w-fit bg-tight-tint text-tight-text">
                  <span className="pill-dot bg-tight" />
                  Email not verified
                </span>
              )}
            </div>
          )}
          {organization && <DetailRow label="Workspace">{organization.name}</DetailRow>}
          {signInMethods && <DetailRow label="Sign-in method">{signInMethods}</DetailRow>}
          <DetailRow label="Time zone" mono>
            {SHOP_TIMEZONE}
          </DetailRow>
          {memberSince && (
            <DetailRow label="Member since" mono>
              {formatDay(memberSince)}
            </DetailRow>
          )}
        </section>

        <div className="flex min-w-0 grow flex-col gap-3">
          <LinkCard
            to="/settings"
            icon={<ClockIcon />}
            title="Shop week"
            detail={
              settings && machines
                ? `${plural(machines.active.length, "machine")} · ${formatHours(machines.weeklyHours)} a week · ${dayRange(settings.workingDays)}`
                : "Loading…"
            }
          />
          <LinkCard
            to="/settings"
            hash="archived"
            icon={<ArchiveIcon />}
            title="Archived jobs"
            detail={archived ? (archived.length > 0 ? plural(archived.length, "job") : "Nothing archived") : "Loading…"}
          />
          {analyticsAvailable && <RecordingSetting />}
          <section className="flex flex-col gap-3 rounded-panel bg-brand-tint p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-[15px] leading-[18px] font-extrabold text-brand-dark">Free pilot</h2>
              <span className="font-mono text-[11px] leading-[14px] font-bold text-brand-dark">NO CARD NEEDED</span>
            </div>
            <p className="text-[13px] leading-[150%]">
              Backplan is free while you're in the pilot. We'll agree a simple monthly price with you before anything is charged.
            </p>
          </section>
          <button
            type="button"
            onClick={() => clerk.signOut()}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-panel border border-dashed border-line px-5 py-4 text-left transition-colors hover:border-quoted hover:bg-bg"
          >
            <span className="text-sm leading-[18px] font-bold text-muted">Sign out of Backplan</span>
            {session && <span className="font-mono text-[11px] leading-[14px] text-subtle">Signed in {formatDayTime(session.createdAt)}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Decision D46: where someone changes their answer to the recording question. */
function RecordingSetting() {
  const choice = useRecordingChoice();
  const on = choice === "yes";
  return (
    <section className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-panel border border-line bg-bg px-5 py-[18px]">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="text-sm leading-[18px] font-bold">Session recording {on ? "is on" : "is off"}</h2>
        <p className="text-[13px] leading-[1.45] text-muted">
          {on
            ? "Replays help us see where Backplan gets in your way. They hide all text and everything you type."
            : "Nothing is recorded in this browser. Turning it on helps us see where Backplan gets in your way."}
        </p>
      </div>
      <button type="button" className={on ? "btn-secondary" : "btn-primary"} onClick={() => setRecordingChoice(on ? "no" : "yes")}>
        {on ? "Turn off" : "Turn on"}
      </button>
    </section>
  );
}

function DetailRow({ label, mono = false, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md bg-surface px-4 py-3.5">
      <span className="text-sm leading-[18px] text-muted">{label}</span>
      <span className={`text-right text-sm leading-[18px] font-bold ${mono ? "font-mono" : ""}`}>{children}</span>
    </div>
  );
}

function LinkCard({ to, hash, icon, title, detail }: { to: LinkProps["to"]; hash?: string; icon: ReactNode; title: string; detail: string }) {
  return (
    <Link
      to={to}
      hash={hash}
      className="flex items-center gap-3.5 rounded-panel border border-line bg-bg px-5 py-[18px] transition-colors hover:border-quoted"
    >
      <span className="flex size-11 shrink-0 items-center justify-center rounded-md bg-surface">{icon}</span>
      <span className="flex min-w-0 grow flex-col gap-[3px]">
        <span className="text-[15px] leading-[18px] font-extrabold">{title}</span>
        <span className="text-[13px] leading-4 text-muted">{detail}</span>
      </span>
      <span className="text-muted">
        <ChevronRightIcon />
      </span>
    </Link>
  );
}

/** "google" → "Google". Clerk reports providers in lower case, sometimes prefixed "oauth_". */
function providerName(provider: string): string {
  const bare = provider.replace(/^oauth_/, "");
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** [1, 2, 3, 4, 5] → "Mon–Fri"; days with gaps are listed: "Mon, Wed, Fri". */
function dayRange(workingDays: readonly number[]): string {
  const positions = WEEK_ORDER.map((d, i) => (workingDays.includes(d) ? i : -1)).filter((i) => i >= 0);
  if (positions.length === 0) return "No working days";
  const first = positions[0]!;
  const last = positions[positions.length - 1]!;
  const contiguous = last - first === positions.length - 1;
  if (contiguous && positions.length > 2) return `${DAY_NAMES[WEEK_ORDER[first]!]}–${DAY_NAMES[WEEK_ORDER[last]!]}`;
  return positions.map((i) => DAY_NAMES[WEEK_ORDER[i]!]).join(", ");
}
